import { useTranslation } from "react-i18next";
import { CalendarClock, UserRound, Users } from "lucide-react";
import { formatDateAndTimeParts } from "../lib/datetime";
import { localizedTitle } from "../lib/localize";
import type { PDDayDetail } from "../types";

const labelClass = "text-[11px] font-semibold uppercase tracking-wider text-app-muted";
const valueClass = "mt-0.5 text-base font-semibold text-app-ink";

// The whole card is read-only on purpose: assignment is owned by the admin
// portal, so the facilitator app only ever displays what was assigned.
export function AssignedPDDayCard({
  detail,
  dayNumber,
}: {
  detail: PDDayDetail;
  dayNumber?: number;
}) {
  const { t, i18n } = useTranslation();
  const when = formatDateAndTimeParts(detail.start, detail.end, i18n.language, t("todaySection"));
  const facilitators =
    detail.facilitators.length > 0
      ? detail.facilitators.map((facilitator) => facilitator.name)
      : [detail.facilitator_name];
  const heading = dayNumber ? t("pdDayOrdinal", { number: dayNumber }) : t("pdDay");
  // Only show the authored title when it adds something to the heading.
  const subtitle = detail.title && detail.title.trim() !== heading ? detail.title : "";
  return (
    <section className="flex flex-col overflow-hidden rounded-xl bg-surface shadow-raised">
      <div className="flex flex-col gap-3 p-4">
        <div className="flex flex-col py-1">
          <span className={labelClass}>{t("pdDay")}</span>
          <span className={valueClass}>{heading}</span>
          {subtitle && <span className="text-sm text-app-copy">{subtitle}</span>}
        </div>
        {detail.modules?.length > 0 && (
          <>
            <div className="h-px w-full bg-app-line" />
            <div className="flex flex-col py-1">
              <span className={labelClass}>{t("modulesLabel")}</span>
              <ol className="mt-0.5 grid gap-1">
                {detail.modules.map((module, index) => (
                  <li key={module.id} className="flex items-start gap-1.5">
                    <span className="text-sm font-semibold tabular-nums text-app-muted">
                      {index + 1}.
                    </span>
                    <span className="text-base font-semibold text-app-ink">
                      {localizedTitle(module, i18n.language)}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          </>
        )}
        <div className="h-px w-full bg-app-line" />
        <div className="flex flex-col py-1">
          <span className={labelClass}>{t("facilitatorsLabel")}</span>
          <ul className="mt-0.5 grid grid-cols-2 gap-x-4 gap-y-1">
            {facilitators.map((name, index) => (
              <li key={name + index} className="flex items-center gap-1.5">
                <UserRound size={16} className="shrink-0 text-app-icon" />
                <span className="text-base font-semibold text-app-ink">{name}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="h-px w-full bg-app-line" />
        <div className="flex flex-col py-1">
          <span className={labelClass}>{t("centerAssigned")}</span>
          <span className={valueClass}>{detail.center}</span>
        </div>
        <div className="h-px w-full bg-app-line" />
        <div className="grid grid-cols-2 gap-4 py-1">
          <div className="flex flex-col">
            <span className={labelClass}>{t("teacherCount")}</span>
            <div className="mt-0.5 flex items-center gap-1">
              <Users size={16} className="text-app-icon" />
              <span className="text-base font-semibold text-app-ink">
                {t("teacherCountValue", { total: detail.teacher_count })}
              </span>
            </div>
          </div>
          <div className="flex flex-col">
            <span className={labelClass}>{t("dateAndTime")}</span>
            <div className="mt-0.5 flex items-start gap-1">
              <CalendarClock size={16} className="mt-0.5 shrink-0 text-app-icon" />
              <span className="flex flex-col">
                <span className="text-base font-semibold text-app-ink">{when.day}</span>
                <span className="text-xs font-medium text-app-copy" dir="ltr">
                  {when.time}
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
